// Modele de donnees de la vue PRESTATAIRE commande-bc (v2).
// Source unique des codes de statut : table `status` du seeder bdc/api (PARTAGEE
// acheteur + prestataire, colonne type_compte). Les ids sont les memes des deux cotes ;
// seul le sens metier change. On reproduit l'enum ici pour garder la case autonome
// (pas d'import cross-case), pas pour en faire un jeu d'ids different.
export enum StatusId {
  BrouillonAcheteur = 1,
  BrouillonPrestataire = 2, // presta : panier inverse, a chiffrer
  AttenteAcceptationRang1 = 3,
  AAccepter = 4, // presta : doit accepter / refuser
  AttenteAcceptationRang2 = 5,
  AttenteAcceptationRang3 = 6,
  Valide = 7, // accepte
  Refuse = 8,
  Expire = 9,
  AttenteOffre = 10,
  AChiffrer = 11, // presta : catalogue editable
  AAnalyser = 12,
  AttenteAnalyse = 13, // presta : offre envoyee, attente acheteur
  Annule = 14,
  PropositionFaite = 15,
}

export type TabKind =
  | 'historique'
  | 'consultation'
  | 'offre'
  | 'notification'
  | 'messagerie'
  | 'evaluation';

// Un onglet est une donnee, pas un libelle a re-parser ni un index a deviner.
export interface TabDescriptor {
  key: string;
  kind: TabKind;
  label: string;
  disabled: boolean;
  inactive: boolean;
}

// Reference du statut (table `status`). Lue par id, jamais par nom.
export interface StatusRef {
  id?: number;
  nom?: string;
}

// Evenement du flux d'historique (onglet Historique). Le backend ne fournit qu'un
// `prefix` textuel comme marqueur d'action + un `label` descriptif.
export interface HistoriqueEvent {
  created_at?: string;
  operateur?: string;
  prefix?: string;
  label?: string;
}

// Operation (marche/bon de commande) telle que VUE par le prestataire : la commande
// la WRAPPE (commande.operation). Champs limites a ce que la case consomme.
export interface OperationRef {
  id?: number;
  nom?: string;
  numero?: string;
  reference?: string;
  // Flag panier inverse cote PRESTA : il vit sur l'operation wrappee (commande.operation),
  // PAS sur commande.transferred (forme acheteur). Cf. ConsultationBC.tsx:587.
  transferred?: boolean;
  besoin?: string;
  adresse?: string;
  code_postal?: string;
  ville?: string;
  visite?: string | number | boolean;
  condition_visite?: string;
  facturation?: string;
  coordonnees_maitrise_ouvrage?: string;
  date_debut_operation?: string;
  date_fin_operation?: string;
  // Descriptif refonte (parite MS), serialise par bdc CommandeResource. Tous nullable.
  nom_ouvrage?: string;
  pays?: string;
  moe_interlocuteur?: string;
  moe_mail?: string;
  moe_telephone?: string;
  niveau_sensibilite?: string;
  signataire_commande?: string;
  phases?: string;
  verification_identites?: string;
  habilitation?: string;
  charte_reglementation?: string;
  clause_suivi_environnemental?: string;
  url_doc_technique?: string;
  modalite_acces_documentation?: string;
  liste_pieces_documentation?: string;
  lot_technique?: { id?: number; nom?: string };
  lot_departement?: { departement?: { id?: number; nom?: string } };
  // Organisme acheteur enrichi par le gateway core (BdCController::showById) : modele complet + adresse.
  organisme?: {
    nom?: string;
    siret?: string;
    type_organisme?: string;
    adresse?: {
      numero_voie?: string;
      type_voie?: string;
      nom_voie?: string;
      code_postal?: string;
      ville?: string;
      pays?: string;
    };
  };
  acheteur?: { prenom?: string; nom?: string; email_contact?: string; telephone?: string };
}

// Buffer de formulaire ajoute par adaptApiData (EntityService) a partir de operation.*.
export interface CommandeFormulaire {
  nomOperation?: string;
  referenceInterne?: string;
  delaisConsultation?: number;
  besoin?: string;
  visitePreavis?: boolean;
  conditionVisite?: string;
  adresse?: string;
  codePostal?: string;
  ville?: string;
  coordonneesMaitrise?: string;
  dateDebutOperation?: string;
  dateFinOperation?: string;
}

// La commande BC cote prestataire (reponse GET /bdc/commande/{id}, adaptee).
export interface CommandeBC {
  id: number;
  id_status?: number;
  status?: StatusRef;
  rang?: number;
  /** Pivot `lot_contrat` : cote presta, la commande est deja scopee a son rang — c'est donc le SIEN. */
  id_contrat?: number;
  expire_at?: string | null;
  transferred?: boolean;
  // Offre technique SAISIE par le prestataire (texte libre <= 5000 car.). Portee par la COMMANDE
  // (par rang, par presta) et exposee a la racine par bdc CommandeResource. Nullable : un brouillon
  // peut rester vide, l'obligation ne s'applique qu'a l'envoi de l'offre.
  offre_technique?: string | null;
  // Horodatage de l'attestation "documents administratifs a jour" (regle 5 : preuve datee au moment
  // de l'engagement). Colonne `commande.attestation_documents_at` (TIMESTAMP nullable) posee par la
  // route dediee : non-null = atteste, null = pas / plus atteste. Le front en deduit l'etat coche
  // initial de la case (regle 6). Serialisee a la racine par bdc CommandeResource.
  attestation_documents_at?: string | null;
  created_at?: string;
  updated_at?: string;
  articles?: unknown[];
  historique?: HistoriqueEvent[];
  contrat?: { entite?: Record<string, unknown> };
  operation?: OperationRef;
  formulaire?: CommandeFormulaire;
}
